import { Component, OnInit } from '@angular/core';
import { BasicList } from '../../classes';
import { LoopBackConfig, OrderApi, LoopBackAuth, CardApi, PartnerApi } from '../../../assets/js/lb-sdk';
import { STORAGE_LOCATIONS, ORDER_STATUS, CARD_STATUS, PAYMENT_TYPE, USER_ROLES } from '../../enums';
import { BsModalService } from 'ngx-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { HasRolePipe } from '../../pipes/has-role.pipe';
import { Event } from '../../interfaces';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent extends BasicList implements OnInit {
  host:string = LoopBackConfig.getPath();
  container:string = STORAGE_LOCATIONS.PRODUCT_IMAGES;
  ORDER_STATUS: typeof ORDER_STATUS = ORDER_STATUS;
  PAYMENT_TYPE: typeof PAYMENT_TYPE = PAYMENT_TYPE;
  USER_ROLES: typeof USER_ROLES = USER_ROLES;
  searchSettings = {
    searchFields: [],
    searchableFields: [
      { name: 'reference', placeholder: 'Reference Number' },
      { name: 'status', placeholder: 'Status', options: ORDER_STATUS }
    ],
    filters: {}
  };

  analytics: {
    activatedCardCount: number,
    totalUnpaidOrders: number,
    totalCardCount: number,
    totalDiscountEarned: number,
    totalCommissionEarned: number,
    totalUnpaid: number
  };
  unpaid;

  processing: {
    [key: string]: boolean
  } = {};

  useScanner = false;

  constructor(
    private ordersApi: OrderApi,
    private modalService: BsModalService,
    private loopBackAuth: LoopBackAuth,
    private cardApi: CardApi,
    private partnerApi: PartnerApi,
    private toast: ToastrService,
    private hasRolePipe: HasRolePipe
  ) {
    super();
    this.searchSettings.searchFields.push(this.searchSettings.searchableFields[0]);
    this.params = {
      where: {
        partnerId: this.getUser().partnerId || undefined
      },
      order: 'dateCreated DESC'
    };
    if (this.getUser() && this.hasRolePipe.transform(this.getUser().roles, this.USER_ROLES.TREASURY)) {
      this.params.where = {
        and: [
          { status: { neq: this.ORDER_STATUS.PAID } },
          { status: { neq: this.ORDER_STATUS.CANCELLED } }
        ]
      };
    }
    this.settings = {
      createComponent: undefined,
      api: this.ordersApi,
      ModalService: this.modalService
    };
    this.analytics = {
      activatedCardCount: undefined,
      totalUnpaidOrders: undefined,
      totalCardCount: undefined,
      totalDiscountEarned: undefined,
      totalCommissionEarned: undefined,
      totalUnpaid: undefined
    };
  }

  ngOnInit() {
    this.getList();
    this.getAnalytics();
  }

  getAnalytics() {
    // AGENT MERCHANT BROKER AIA EMPLOYEE
    if (this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, 'between', USER_ROLES.BROKER)) {
      this.getOldestUnpaid();
      this.getActivatedCardCount(this.getUser().id);
      this.getTotalCardCount(this.getUser().id);
      this.getUnpaidOrders(this.getUser().partnerId);
      this.getTotalUnpaid(this.getUser().id);
    }
    // MERCHANT
    if (this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.GENERAL_MERCHANT, 'between', USER_ROLES.ONLINE_RETAILER)) {
      this.getTotalDiscountEarned();
    }
    // AGENT AIA EMPLOYEE
    if (this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.AUH, 'between', USER_ROLES.BROKER)) {
      this.getTotalDiscountEarned();
      if (this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.AUH, 'between', USER_ROLES.AUM)) {
        this.getTotalCommissionEarned();
      }
    }
    // ADMIN
    if (
      this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.ADMIN) ||
      // this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.ALTERNATIVE_CHANNEL) ||
      this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.BDM)
    ) {
      this.getActivatedCardCount();
      this.getTotalCardCount();
      this.getUnpaidOrders();
      this.getAllUnpaid();
    }
    if (
      this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.ADMIN) ||
      this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.ALTERNATIVE_CHANNEL)
    ) {
      this.getOldestUnpaid();
    }
  }

  getOldestUnpaid() {
    this.loading.unpaid = true;
    var filter: any = {
      where: {
        and: [
          { or: [
            { status: ORDER_STATUS.PENDING },
            { status: ORDER_STATUS.PARTIALLY_PAID }
          ] }
        ]
      },
      limit: 1,
      order: 'dateCreated ASC'
    };
    if (this.getUser().partnerId) {
      filter.where.and.push({ partnerId: this.getUser().partnerId });
    }
    this.ordersApi
      .find(filter)
      .pipe(finalize(() => {
        this.loading.unpaid = false;
      }))
      .subscribe(unpaids => {
        this.unpaid = unpaids && unpaids[0];
      }, error => {
        this.toast.error('Fetching oldest unpaid order failed', 'Order');
      });
  }

  getActivatedCardCount(userId?: string) {
    this.cardApi
      .count({
        status: CARD_STATUS.ACTIVATED,
        userId: userId
      })
      .subscribe(response => {
        this.analytics.activatedCardCount = response.count;
      }, error => {
        this.toast.error('Fetching activated card count failed', 'Analytics');
      });
  }

  getTotalCardCount(userId?: string) {
    this.cardApi
      .count({
        userId: userId
      })
      .subscribe(response => {
        this.analytics.totalCardCount = response.count;
      }, error => {
        this.toast.error('Fetching total card count failed', 'Analytics');
      });
  }

  getUnpaidOrders(partnerId?: string) {
    const filter: any = {
      and: [
        { or: [
          { status: ORDER_STATUS.PENDING },
          { status: ORDER_STATUS.PARTIALLY_PAID }
        ] }
      ]
    };
    if (this.getUser().partnerId) {
      filter.and.push({ partnerId: this.getUser().partnerId });
    }
    this.ordersApi
      .count(filter)
      .subscribe(response => {
        this.analytics.totalUnpaidOrders = response.count;
      }, error => {
        this.toast.error('Fetching total pending orders failed', 'Analytics');
      });
  }

  getTotalDiscountEarned() {
    this.partnerApi
      .getTotalDiscountEarned(this.getUser().id, {})
      .subscribe(response => {
        this.analytics.totalDiscountEarned = response.totalDiscountEarned;
      }, error => {
        this.toast.error('Fetching total discount earned failed', 'Analytics');
      });
  }

  getTotalCommissionEarned() {
    this.partnerApi
      .getTotalCommissionEarned(this.getUser().id, {})
      .subscribe(response => {
        this.analytics.totalCommissionEarned = response.totalCommissionEarned;
      }, error => {
        this.toast.error('Fetching total commission earned failed', 'Analytics');
      });
  }

  getTotalUnpaid(userId?: string) {
    this.partnerApi
      .getTotalUnpaid(userId || '', {})
      .subscribe(response => {
        this.analytics.totalUnpaid = response.amount;
      }, error => {
        this.toast.error('Fetching total unpaid amount failed', 'Analytics');
      });
  }

  getAllUnpaid() {
    this.partnerApi
      .getAllUnpaid({})
      .subscribe(response => {
        this.analytics.totalUnpaid = response.amount;
      }, error => {
        this.toast.error('Fetching total unpaid amount failed', 'Analytics');
      });
  }

  getUser() {
    return this.loopBackAuth.getToken() ? this.loopBackAuth.getCurrentUserData() : undefined;
  }

  hasPaymentType(type) {
    if (!this.getUser()) {
      return false;
    }
    return (this.getUser().roles || [])
      .filter(role => {
        return role.paymentType === type;
      }).length > 0;
  }

  eventListener(event: Event) {
    switch (event.event) {
      case 'delete:success':
      this.getAnalytics();
      case 'initialized':
      case 'createModal:completed':
      case 'editModal:completed':
      this.getList();
      break;
      default:
        break;
    }
  }

  getOr(order: any) {
    if (
      !this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.BDM) ||
      order.status !== ORDER_STATUS.PAID ||
      (order.ORNumber && order.ORNumber !== '')
    ) {
      return;
    }
    this.processing[order.id + '_sap'] = true;
    this.ordersApi
      .generateOr(order.id)
      .pipe(finalize(() => {
        this.processing[order.id + '_sap'] = false;
      }))
      .subscribe(data => {
        if (data && data.ORNumber) {
          this.toast.success('OR successfully generated', 'Reprocess OR');
        } else {
          this.toast.error('Failed to generate OR of order ' + order.reference, 'Reprocess OR');
        }
        this.getList();
      }, error => {
        this.toast.error(
          (error && error.message) ?
          (error.message.indexOf('Connection failed.') >= 0 ? 'SAP connection failed' : error.message) :
          ('Failed to generate OR of order ' + order.reference), 'Reprocess OR');
      });
  }
}
